import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import "./Footer.css";
import { CmsText } from "../../RichTextContent/RichTextContent";
import { useFooterSection } from "../../../context/GlobalLayoutContext";
import {
  getLinkGroupFromFields,
  getSocialsFromFields,
} from "../../../services/contentApi";
import {
  resolveSocialIconSrc,
  SOCIAL_PLATFORM_ICONS,
  SOCIAL_PLATFORM_OPTIONS,
} from "../../../features/cms/footerSocials/socialPlatforms";

import logo from "../../../assets/images/logo.svg";

export {
  resolveSocialIconSrc,
  SOCIAL_PLATFORM_ICONS,
  SOCIAL_PLATFORM_OPTIONS,
};

function resolveSocialHref(url) {
  if (!url) return "#";
  if (/^https?:\/\//i.test(url) || url.startsWith("mailto:")) return url;
  if (url.startsWith("/")) return url;
  return `https://${url}`;
}

const Footer = () => {
  const { section, loading } = useFooterSection();

  const socials = useMemo(() => {
    if (!section?.fields) return [];
    return getSocialsFromFields(section.fields);
  }, [section]);

  if (loading || !section?.fields) return null;

  const fields = section.fields;
  const company = getLinkGroupFromFields(fields, "company");
  const resources = getLinkGroupFromFields(fields, "resources");
  const legal = getLinkGroupFromFields(fields, "legal");
  const columns = [company, resources, legal];

  return (
    <footer className="footer">
      <div className="footer-top">
        <div className="footer-left">
          <Link to="/" className="footer-logo-link">
            <img src={logo} alt="logo" className="footer-logo" />
          </Link>
          <p>
            <CmsText content={fields.tagline_line1} as="span" />
            <br />
            <CmsText content={fields.tagline_line2} as="span" />
          </p>
        </div>

        <div className="footer-links">
          {columns.map((col) =>
            col.links.length > 0 ? (
              <div className="footer-col" key={col.heading}>
                <CmsText content={col.heading} as="span" />
                {col.links.map((link) => (
                  <Link key={link.url + link.text} to={link.url}>
                    <CmsText content={link.text} as="span" />
                  </Link>
                ))}
              </div>
            ) : null
          )}
        </div>
      </div>

      <div className="footer-bottom">
        <p>
          © {new Date().getFullYear()}{" "}
          <CmsText content={fields.copyright_text} as="span" />
        </p>

        {socials.length > 0 ? (
          <div className="footer-socials">
            {socials.map((social) => {
              const icon = resolveSocialIconSrc(social);
              const href = resolveSocialHref(social.url);
              const isExternal = /^https?:\/\//i.test(href);
              const label = social.label || social.platform || "Social";

              if (!isExternal && href.startsWith("/")) {
                return (
                  <Link
                    key={`social-${social.index}`}
                    to={href}
                    className="footer-socials__link"
                    aria-label={label}
                  >
                    <img src={icon} alt="" />
                  </Link>
                );
              }

              return (
                <a
                  key={`social-${social.index}`}
                  href={href}
                  className="footer-socials__link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                >
                  <img src={icon} alt="" />
                </a>
              );
            })}
          </div>
        ) : null}
      </div>
    </footer>
  );
};

export default Footer;
